import React, { useState, useEffect } from "react";
import Input from "../../Input/Input";
import Button from "../../Button/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  setCommunicationsBudget,
  selectCommunicationsBudget,
} from "../../../redux/slices/filterSlice";
import {
  setPmsBudget,
  selectPmsBudget,
} from "../../../redux/slices/pmsFilterSlice";
import CheckIcon from "../../../assets/svg/check-icon";
import { BudgetRange, BudgetDuration, Currency } from "../types";
import { RootState } from "../../../redux/store";
import { getBudgetInputPlaceholder, getBudgetList } from "../utils/constants";

interface QuickFilter {
  label: string;
  min_budget: number;
  max_budget: number;
  duration: BudgetDuration;
  currency: Currency;
}

const BudgetFilter = ({
  onClose,
  isPms = false,
  isMultiSelect = true,
}: {
  onClose: () => void;
  isPms?: boolean;
  isMultiSelect?: boolean;
}) => {
  const dispatch = useDispatch();
  const communicationsBudget = useSelector(selectCommunicationsBudget);
  const pmsBudget = useSelector(selectPmsBudget);
  const budget = isPms ? pmsBudget : communicationsBudget;
  const client = useSelector((state: RootState) => state?.auth?.client);

  const [selectedQuickFilters, setSelectedQuickFilters] = useState<
    QuickFilter[]
  >([]);
  const [minValue, setMinValue] = useState<string>("");
  const [maxValue, setMaxValue] = useState<string>("");
  const [isCustomMode, setIsCustomMode] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>("");

  const quickFilters = getBudgetList(client?.region);

  // Initialize state from Redux on mount
  useEffect(() => {
    if (budget.value && budget.value.length > 0) {
      // Check if all values match quick filters
      const matchedQuickFilters: QuickFilter[] = [];
      let hasCustomValue = false;

      budget.value.forEach((range: BudgetRange) => {
        const matchingFilter = quickFilters.find(
          (qf) =>
            qf.min_budget === range.min_budget &&
            qf.max_budget === range.max_budget
        );
        if (matchingFilter) {
          matchedQuickFilters.push(matchingFilter);
        } else {
          hasCustomValue = true;
        }
      });

      if (hasCustomValue && budget.value.length === 1) {
        // It's a custom value
        setIsCustomMode(true);
        setMinValue(budget.value[0].min_budget.toString());
        setMaxValue(budget.value[0].max_budget.toString());
      } else if (matchedQuickFilters.length > 0) {
        // It's quick filter selections
        setSelectedQuickFilters(matchedQuickFilters);
        setIsCustomMode(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isQuickFilterSelected = (filter: QuickFilter) => {
    return selectedQuickFilters.some(
      (qf) =>
        qf.min_budget === filter.min_budget &&
        qf.max_budget === filter.max_budget
    );
  };

  const handleQuickFilterClick = (filter: QuickFilter) => {
    // Clear custom mode when selecting quick filters
    setIsCustomMode(false);
    setMinValue("");
    setMaxValue("");
    setValidationError("");

    if (isQuickFilterSelected(filter)) {
      // Remove filter
      setSelectedQuickFilters((prev) =>
        prev.filter(
          (qf) =>
            !(
              qf.min_budget === filter.min_budget &&
              qf.max_budget === filter.max_budget
            )
        )
      );
    } else {
      if (isMultiSelect) {
        setSelectedQuickFilters((prev) => [...prev, filter]);
      } else {
        setSelectedQuickFilters([filter]);
      }
    }
  };

  const handleCustomInputChange = (field: "min" | "max", value: string) => {
    // Clear quick filter selections when typing custom values
    if (selectedQuickFilters.length > 0) {
      setSelectedQuickFilters([]);
    }
    setIsCustomMode(true);
    
    if (field === "min" && parseInt(value) < 0) {
      setValidationError("Minimum value cannot be less than 0");
      return;
    }
    if (field === "max" && parseInt(value) < 0) {
      setValidationError("Maximum value cannot be less than 0");
      return;
    }

    const newMin = field === "min" ? value : minValue;
    const newMax = field === "max" ? value : maxValue;

    if (field === "min") {
      setMinValue(value);
    } else {
      setMaxValue(value);
    }

    // Validate the range
    const minNum = parseInt(newMin) || 0;
    const maxNum = parseInt(newMax) || 0;

    if (newMin && newMax && minNum > 0 && maxNum > 0) {
      if (minNum > maxNum) {
        setValidationError(
          "Minimum value cannot be greater than maximum value"
        );
      } else {
        setValidationError("");
      }
    } else {
      setValidationError("");
    }
  };

  const handleApply = () => {
    if (isCustomMode) {
      // Apply custom range
      const min_budget = parseInt(minValue) || 0;
      const max_budget = parseInt(maxValue) || 0;

      if (min_budget > 0 || max_budget > 0) {
        const budgetPayload = {
            value: [
              {
                min_budget,
                max_budget,
                duration: BudgetDuration.WEEKLY,
                currency: Currency.GBP,
              },
            ],
            displayValue: [`£${min_budget} - £${max_budget}`],
        };

        dispatch(isPms ? setPmsBudget(budgetPayload) : setCommunicationsBudget(budgetPayload));
      }
    } else if (selectedQuickFilters.length > 0) {
      // Apply quick filters
      const budgetRanges: BudgetRange[] = selectedQuickFilters.map((qf) => ({
        min_budget: qf.min_budget,
        max_budget: qf.max_budget,
        duration: qf.duration,
        currency: qf.currency,
      }));

      // Generate display value as array
      const displayValue = selectedQuickFilters.map((qf) => qf.label);

      const budgetPayload = {
          value: budgetRanges,
          displayValue,
      };

      dispatch(isPms ? setPmsBudget(budgetPayload) : setCommunicationsBudget(budgetPayload));
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isApplyDisabled =
    (selectedQuickFilters.length === 0 &&
      (!minValue || !maxValue ||
        (parseInt(minValue) === 0 && parseInt(maxValue) === 0))) ||
    validationError !== "";

  return (
    <div className="flex flex-col gap-2 mt-2">
      <div className="flex flex-row items-center justify-between px-3">
        <h2 className="text-md text-verba-field-label dark:text-verba-field-label-dk">
          Quick Filters
        </h2>
        <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm">
          SELECTED ({selectedQuickFilters.length})
        </span>
      </div>
      {quickFilters.map((option) => (
        <div
          key={option.label}
          onClick={() => handleQuickFilterClick(option)}
          className={`flex items-center justify-between p-3 mx-3 text-md gap-2 cursor-pointer border border-border-gray dark:border-border-gray-dk rounded-lg text-heading-text dark:text-heading-text-dk ${
            isQuickFilterSelected(option)
              ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-verba-blue dark:border-verba-blue-dk"
              : ""
          }`}
        >
          {option.label}
          <div
            className={`shrink-0 w-[14px] h-[14px] border border-gray-100 dark:border-gray-100-dk rounded-[3px] flex items-center justify-center ${
              isQuickFilterSelected(option)
                ? "bg-verba-blue text-pure-white dark:bg-verba-blue-dk dark:text-pure-white-dk border-verba-blue dark:border-verba-blue-dk"
                : ""
            }`}
          >
            {isQuickFilterSelected(option) && (
              <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
            )}
          </div>
        </div>
      ))}

      <div className="flex flex-col gap-3 border-t border-border-gray dark:border-border-gray-dk mt-2 px-3">
        <span className="text-md text-verba-field-label dark:text-verba-field-label-dk pt-2">
          Custom budget range
        </span>
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center gap-3.5 w-full">
            <Input
              type="number"
              placeholder={getBudgetInputPlaceholder(client?.region, "min")}
              value={minValue}
              min={0}
              onChange={(e) => handleCustomInputChange("min", e.target.value)}
              inputClassName="w-[171px] p-2"
              secondaryContainerClassName="w-[171px] border border-border-gray dark:border-border-gray-dk rounded-lg"
            />
            <Input
              type="number"
              placeholder={getBudgetInputPlaceholder(client?.region, "max")}
              value={maxValue}
              min={0}
              onChange={(e) => handleCustomInputChange("max", e.target.value)}
              inputClassName="w-[171px] p-2"
              secondaryContainerClassName="w-[171px] border border-border-gray dark:border-border-gray-dk rounded-lg"
            />
          </div>
          {validationError && (
            <p className="text-sm text-verba-red-alert dark:text-verba-red-alert-dk">
              {validationError}
            </p>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4 px-3">
        <Button
          innerText="Cancel"
          onClick={handleCancel}
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          innerText="Apply"
          onClick={handleApply}
          disabled={isApplyDisabled}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </div>
  );
};

export default BudgetFilter;
